import apiClient from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import { uploadAdminFiles } from "@/lib/files.api";
import type {
  LibraryItemApi,
  LibraryItemsResponse,
  LibraryItemsMeta,
  LibraryFolderItem,
  LibraryFileItem,
  LibraryVideoLinkItem,
  LibraryItem,
  CreateFolderPayload,
  UploadDocumentParams,
  CreateVideoLinkPayload,
} from "../types/library.types";

const ADMIN_LIBRARY_ITEMS_ENDPOINT = "/api/admin/library/items";
const CLIENT_LIBRARY_ITEMS_ENDPOINT = "/api/client/library/items";
const LIBRARY_FOLDERS_ENDPOINT = "/api/admin/library/items/folders";
const LIBRARY_FILES_ENDPOINT = "/api/admin/library/items/files";
const LIBRARY_VIDEO_LINKS_ENDPOINT = "/api/admin/library/items/video-links";
function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function mimeToFileType(mimeType: string, originalName?: string): string {
  if (originalName) {
    const ext = originalName.split(".").pop()?.toLowerCase();
    if (ext) return ext;
  }
  const map: Record<string, string> = {
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "text/plain": "txt",
    "application/vnd.ms-powerpoint": "ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
    "image/png": "png",
    "image/jpeg": "jpg",
  };
  return map[mimeType] ?? "document";
}

function mapApiItemToFolder(item: LibraryItemApi): LibraryFolderItem {
  const uploadedBy = item.createdByUser
    ? [item.createdByUser.firstName, item.createdByUser.lastName].filter(Boolean).join(" ")
    : undefined;
  return {
    id: item.id,
    name: item.name,
    lastUpdatedFormatted: formatDate(item.updatedAt),
    createdAt: item.createdAt,
    uploadedBy,
  };
}

function mapApiItemToFile(item: LibraryItemApi): LibraryFileItem {
  const uploadedBy = item.createdByUser
    ? [item.createdByUser.firstName, item.createdByUser.lastName].filter(Boolean).join(" ")
    : "";
  const name = item.file?.originalName ?? item.name;
  const fileType = item.file ? mimeToFileType(item.file.mimeType, item.file.originalName) : "document";
  const fileSize = item.file ? formatFileSize(item.file.size) : "—";
  return {
    id: item.id,
    name,
    fileType,
    uploadedBy,
    lastUpdatedFormatted: formatDate(item.updatedAt),
    fileSize,
    createdAt: item.createdAt,
    downloadUrl:
      item.downloadUrl ??
      item.file?.downloadUrl ??
      (item as { download_url?: string }).download_url ??
      (item.file as { download_url?: string } | undefined)?.download_url,
  };
}

function mapApiItemToVideoLink(item: LibraryItemApi): LibraryVideoLinkItem {
  const uploadedBy = item.createdByUser
    ? [item.createdByUser.firstName, item.createdByUser.lastName].filter(Boolean).join(" ")
    : "";
  return {
    id: item.id,
    name: item.name,
    externalUrl: item.externalUrl ?? (item as { external_url?: string }).external_url ?? "",
    uploadedBy,
    lastUpdatedFormatted: formatDate(item.updatedAt),
    createdAt: item.createdAt,
  };
}

export interface GetLibraryItemsParams {
  parentItemId?: string;
  /** Verilmezse backend tek listede klasör + dosya + video linki döner */
  type?: "folder" | "file" | "videoLink";
  page?: number;
  limit?: number;
  search?: string;
  updatedAfter?: string;
  /** name | createdAt | type | updatedAt — verilmezse backend updatedAt, desc kullanır */
  sortBy?: "name" | "createdAt" | "type" | "updatedAt";
  sortOrder?: "asc" | "desc";
  scope?: "admin" | "client";
}

export interface GetLibraryItemsResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

function parseResponseData(raw: LibraryItemsResponse): {
  items: LibraryItemApi[];
  meta: { page: number; limit: number; total: number; totalPages: number };
} {
  const fallbackMeta = { page: 1, limit: 20, total: 0, totalPages: 0 };
  const meta = raw.meta ?? fallbackMeta;
  const data = raw.data;

  if (Array.isArray(raw.items)) {
    return { items: raw.items, meta };
  }
  if (Array.isArray(data)) {
    const isItemArray = data.length === 0 || (data[0] && "type" in data[0] && "id" in data[0]);
    if (isItemArray) {
      return { items: data as LibraryItemApi[], meta };
    }
  }
  if (data && typeof data === "object" && "items" in data) {
    const d = data as { items?: LibraryItemApi[]; meta?: LibraryItemsMeta };
    return {
      items: d.items ?? [],
      meta: d.meta ?? meta,
    };
  }
  return { items: [], meta };
}

function mapApiItemToItem(api: LibraryItemApi): LibraryItem {
  if (api.type === "folder") {
    return { kind: "folder", ...mapApiItemToFolder(api) };
  }
  if (api.type === "videoLink") {
    return { kind: "videoLink", ...mapApiItemToVideoLink(api) };
  }
  return { kind: "file", ...mapApiItemToFile(api) };
}

export const libraryApi = {
  getItems: async (
    params: GetLibraryItemsParams
  ): Promise<
    | GetLibraryItemsResult<LibraryFolderItem>
    | GetLibraryItemsResult<LibraryFileItem>
    | GetLibraryItemsResult<LibraryVideoLinkItem>
    | GetLibraryItemsResult<LibraryItem>
  > => {
    const query: Record<string, string | number | undefined> = {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    };
    if (params.type) query.type = params.type;
    if (params.parentItemId) query.parentItemId = params.parentItemId;
    if (params.search?.trim()) query.search = params.search.trim();
    if (params.updatedAfter) query.updatedAfter = params.updatedAfter;
    if (params.sortBy) query.sortBy = params.sortBy;
    if (params.sortOrder) query.sortOrder = params.sortOrder;

    const endpoint =
      params.scope === "client"
        ? CLIENT_LIBRARY_ITEMS_ENDPOINT
        : ADMIN_LIBRARY_ITEMS_ENDPOINT;
    const { data: responseData } = await apiClient.get<LibraryItemsResponse>(
      endpoint,
      { params: query }
    );

    const { items, meta } = parseResponseData(responseData);

    if (params.type === "folder") {
      const data = items.map(mapApiItemToFolder);
      return { data, total: meta.total, page: meta.page, limit: meta.limit };
    }
    if (params.type === "file") {
      const data = items.map(mapApiItemToFile);
      return { data, total: meta.total, page: meta.page, limit: meta.limit };
    }
    if (params.type === "videoLink") {
      const data = items.map(mapApiItemToVideoLink);
      return { data, total: meta.total, page: meta.page, limit: meta.limit };
    }
    const data = items.map(mapApiItemToItem);
    return { data, total: meta.total, page: meta.page, limit: meta.limit };
  },

  /**
   * POST /api/admin/library/items/folders – yeni klasör oluşturur.
   * @returns Oluşan klasörün id'si (içine dosya eklemek için kullanılır).
   */
  createFolder: async (payload: CreateFolderPayload): Promise<{ id: string }> => {
    const body: Record<string, unknown> = {
      name: payload.name,
      shareType: payload.shareType,
    };
    if (payload.parentItemId != null && payload.parentItemId !== "") {
      body.parentItemId = payload.parentItemId;
    }
    if (payload.sharedUserIds?.length) body.sharedUserIds = payload.sharedUserIds;
    if (payload.sharedDepartmentIds?.length) body.sharedDepartmentIds = payload.sharedDepartmentIds;
    const { data } = await apiClient.post<{ data?: { id: string }; id?: string }>(LIBRARY_FOLDERS_ENDPOINT, body);
    const id = data?.data?.id ?? (data as { id?: string })?.id;
    if (!id) throw new Error(i18n.t("libraryApi.folderCreatedNoId"));
    return { id };
  },

  /**
   * Belge ekler: fileIds verilmişse doğrudan onları kullanır; yoksa önce POST /api/admin/files/upload
   * ile dosyaları yükler, dönen id'lerle POST /api/admin/library/items/files'e body gönderir.
   */
  uploadDocuments: async (params: UploadDocumentParams): Promise<void> => {
    let fileIds: string[];
    if (params.fileIds && params.fileIds.length > 0) {
      fileIds = params.fileIds;
    } else if (params.files && params.files.length > 0) {
      const uploaded = await uploadAdminFiles(params.files);
      if (uploaded.length === 0) throw new Error(i18n.t("libraryApi.uploadFailed"));
      fileIds = uploaded.map((f) => f.id);
    } else {
      throw new Error("files veya fileIds gerekli");
    }

    const body: Record<string, unknown> = {
      fileIds,
      shareType: params.shareType,
    };
    if (params.parentItemId != null && params.parentItemId !== "") {
      body.parentItemId = params.parentItemId;
    }
    if (params.sharedUserIds?.length) body.sharedUserIds = params.sharedUserIds;
    if (params.sharedDepartmentIds?.length) body.sharedDepartmentIds = params.sharedDepartmentIds;

    await apiClient.post(LIBRARY_FILES_ENDPOINT, body);
  },

  /**
   * POST /api/admin/library/items/video-links – YouTube video linki oluşturur.
   */
  createVideoLink: async (payload: CreateVideoLinkPayload): Promise<void> => {
    const body: Record<string, unknown> = {
      name: payload.name,
      url: payload.url,
      shareType: payload.shareType,
    };
    if (payload.parentItemId != null && payload.parentItemId !== "") {
      body.parentItemId = payload.parentItemId;
    }
    if (payload.sharedUserIds?.length) body.sharedUserIds = payload.sharedUserIds;
    if (payload.sharedDepartmentIds?.length) body.sharedDepartmentIds = payload.sharedDepartmentIds;
    if (payload.sharedDepartmentTypes?.length) {
      body.sharedDepartmentTypes = payload.sharedDepartmentTypes;
    }

    await apiClient.post(LIBRARY_VIDEO_LINKS_ENDPOINT, body);
  },

  /**
   * DELETE /api/admin/library/items/{id} – Kütüphane öğesini (klasör, dosya veya video linki) siler.
   */
  deleteItem: async (id: string): Promise<void> => {
    await apiClient.delete(`${ADMIN_LIBRARY_ITEMS_ENDPOINT}/${id}`);
  },
};
